import React, { useState, useEffect } from 'react';
import subUserService from '../../services/SubUserService';
import { Shield, Save, RefreshCw, CheckCircle2, XCircle, Users, Loader } from 'lucide-react';

const MODULES = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'students', label: 'Students', icon: '🎓' },
  { key: 'teachers', label: 'Teachers', icon: '👨‍🏫' },
  { key: 'academics', label: 'Academics', icon: '📚' },
  { key: 'attendance', label: 'Attendance', icon: '📋' },
  { key: 'examination', label: 'Examinations', icon: '📝' },
  { key: 'fees', label: 'Fees', icon: '💰' },
  { key: 'library', label: 'Library', icon: '📖' },
  { key: 'hostel', label: 'Hostel', icon: '🏨' },
  { key: 'transport', label: 'Transport', icon: '🚌' },
  { key: 'id-cards', label: 'ID Cards', icon: '🪪' },
  { key: 'marks-cards', label: 'Marks Cards', icon: '📑' },
  { key: 'certificates', label: 'Certificates', icon: '📜' },
  { key: 'smart-alerts', label: 'Smart Alerts', icon: '🔔' },
  { key: 'reports', label: 'Reports', icon: '📈' },
  { key: 'settings', label: 'Settings', icon: '⚙️' },
];

const PermissionsMatrix = ({ showNotification }) => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  // Map of userId -> Set of module keys
  const [permissionsMap, setPermissionsMap] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await subUserService.getAllStaff();
      if (res && res.success && res.users) {
        setStaffList(res.users);
        const map = {};
        for (const user of res.users) {
          map[user.id] = new Set(user.modulePermissions || ['dashboard']);
        }
        setPermissionsMap(map);
      }
    } catch (err) {
      console.error('Failed to load staff for permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (userId, moduleKey) => {
    setPermissionsMap(prev => {
      const updated = { ...prev };
      const perms = new Set(updated[userId] || []);
      if (moduleKey === 'dashboard') return prev; // Dashboard is always on
      if (perms.has(moduleKey)) {
        perms.delete(moduleKey);
      } else {
        perms.add(moduleKey);
      }
      updated[userId] = perms;
      return updated;
    });
  };

  const selectAll = (userId) => {
    setPermissionsMap(prev => {
      const updated = { ...prev };
      updated[userId] = new Set(MODULES.map(m => m.key));
      return updated;
    });
  };

  const deselectAll = (userId) => {
    setPermissionsMap(prev => {
      const updated = { ...prev };
      updated[userId] = new Set(['dashboard']);
      return updated;
    });
  };

  const savePermissions = async (userId) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const perms = Array.from(permissionsMap[userId] || []);
      const res = await subUserService.updateUserPermissions(userId, { modulePermissions: perms });
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Saved', 'Permissions updated successfully.');
      } else {
        throw new Error(res?.error || 'Save failed');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '60px', textAlign: 'center' }}>
        <Loader size={28} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '12px' }}>Loading permissions matrix...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (staffList.length === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '60px', textAlign: 'center' }}>
        <Users size={40} color="#cbd5e1" />
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginTop: '12px' }}>No Staff Users Found</h3>
        <p style={{ color: '#64748b', fontSize: '14px' }}>Create staff users first, then come back to assign module permissions.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={20} color="#2563eb" /> Access Control & Screen Permissions Matrix
          </h3>
          <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
            Toggle modules on/off per staff user. Click <strong>Save</strong> to apply changes.
          </p>
        </div>
        <button onClick={loadData} style={{ padding: '8px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
          <RefreshCw size={14} /> Reload
        </button>
      </div>

      {/* Matrix Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1, minWidth: '200px' }}>
                Staff Member
              </th>
              {MODULES.map(mod => (
                <th key={mod.key} style={{ padding: '10px 6px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '11px', minWidth: '70px' }}>
                  <div>{mod.icon}</div>
                  <div style={{ marginTop: '2px' }}>{mod.label}</div>
                </th>
              ))}
              <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', color: '#475569', minWidth: '120px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {staffList.map(user => {
              const userPerms = permissionsMap[user.id] || new Set();
              const isSaving = saving[user.id];
              return (
                <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 16px', position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: '13px', flexShrink: 0 }}>
                        {(user.firstName || user.username || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                          {user.firstName || ''} {user.lastName || ''}{!user.firstName && !user.lastName ? user.username : ''}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '11px' }}>
                          {(user.role || '').replace(/_/g, ' ')} • {user.employeeId || user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  {MODULES.map(mod => {
                    const isOn = userPerms.has(mod.key);
                    const isDashboard = mod.key === 'dashboard';
                    return (
                      <td key={mod.key} style={{ padding: '8px 6px', textAlign: 'center' }}>
                        <button
                          onClick={() => !isDashboard && togglePermission(user.id, mod.key)}
                          disabled={isDashboard}
                          style={{
                            width: '28px', height: '28px', borderRadius: '6px', border: 'none',
                            background: isOn ? '#dcfce7' : '#fee2e2',
                            color: isOn ? '#16a34a' : '#dc2626',
                            cursor: isDashboard ? 'not-allowed' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s', opacity: isDashboard ? 0.6 : 1
                          }}
                          title={isDashboard ? 'Dashboard is always enabled' : isOn ? `Revoke ${mod.label}` : `Grant ${mod.label}`}
                        >
                          {isOn ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                        </button>
                      </td>
                    );
                  })}
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button onClick={() => selectAll(user.id)} style={{ fontSize: '10px', padding: '3px 8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>All</button>
                      <button onClick={() => deselectAll(user.id)} style={{ fontSize: '10px', padding: '3px 8px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>None</button>
                      <button onClick={() => savePermissions(user.id)} disabled={isSaving} style={{ fontSize: '10px', padding: '3px 8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px', opacity: isSaving ? 0.7 : 1 }}>
                        <Save size={10} /> {isSaving ? '...' : 'Save'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Legend */}
      <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', gap: '20px', fontSize: '12px', color: '#64748b' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle2 size={14} color="#16a34a" /> Granted</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><XCircle size={14} color="#dc2626" /> Denied</span>
        <span>Dashboard is always granted by default.</span>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
